import { DestroyRef, Directive, inject, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, scan, startWith } from 'rxjs';
import {
  JournalStateStore,
  JournalState,
  ReduxAction,
  JournalFactory,
  StateReducer,
  JournalQueryResult,
} from '../common';
import { InitiantedJournalStateReducersAction, JournalActionTypes } from './journal.actions';
import { initialJournalState } from './journal.state';
import { isExists } from '../utils/is.utils';
import { combineReducers, diffMetaReducer, makeJournalReducer } from '../utils';
import { getJournalReducer } from './journal.reducer';
import { publishRef } from '../utils/rxjs.utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const InitReducersAction: InitiantedJournalStateReducersAction = {
  type: JournalActionTypes.InitiantedJournalStateReducers,
};

interface JournalStoreConfig<T extends JournalState = JournalState> {
  initialState?: T;
  reducer?: StateReducer<T>;
  isDebug?: boolean;
}

export abstract class JournalStateStoreService<
  TState extends JournalState = JournalState,
> implements JournalStateStore<TState> {
  protected destroyRef = inject(DestroyRef);
  protected readonly actions$: Subject<ReduxAction> = new Subject();

  private readonly state$: Observable<TState>;

  constructor(
    private factory: JournalFactory,
    config: JournalStoreConfig<TState> = {},
  ) {
    const initialState: TState = isExists(config?.initialState)
      ? (config.initialState as TState)
      : (initialJournalState as TState);

    const defaultQueryResult = this.factory.getEmptyJournalQueryResult();

    const journalMetaReducer = makeJournalReducer<TState>(defaultQueryResult, config, initialState);

    const actions$: Observable<ReduxAction> = this.actions$.asObservable().pipe(startWith(InitReducersAction));

    this.state$ = actions$.pipe(
      scan<ReduxAction, TState>(journalMetaReducer, initialState),
      takeUntilDestroyed(),
      publishRef,
    );

    this.state$.subscribe();
  }

  getState(): Observable<TState> {
    return this.state$;
  }

  dispatch(action: ReduxAction): void {
    this.actions$.next(action);
  }

  addEffect(effect$: Observable<ReduxAction>): void {
    effect$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((action) => this.dispatch(action));
  }
}
